/**
 * Tablets — the tenant-facing view of their own front-desk device links.
 *
 * WHY this screen exists: the device credential (`ptd_…`) has shipped since the
 * tablet-link work, but the only place to mint or revoke one was the OPS
 * console. So a tenant manager whose tablet walked out of the building had to
 * email Parkify to cut off a credential that validates parking on their own
 * property — a revocation gated on someone else's business hours.
 *
 * This is the manager surface of the tenant portal's two-tier split, and that
 * split is enforced by the SERVER, not by this file: `/api/tenants/me/devices`
 * is excluded from the Worker's `isDeviceRouteAllowed`, so a tablet presenting
 * its own credential gets a 403 here no matter what the UI renders. The
 * `kind === 'device'` bail-out below is a second line, not the line — App.tsx
 * already never routes a tablet to this tab.
 *
 * The plaintext credential is returned exactly ONCE, at creation. Everything
 * that needs it — the copyable URL and the QR — lives in that one-time panel;
 * afterwards the only recovery is minting a new link and revoking the old one.
 */
import { useEffect, useState, type CSSProperties } from 'react'
import type { TenantSession } from '../lib/session'
import {
  listDevices, createDevice, revokeDevice, listValidations, tabletLinkUrl,
  type TenantDevice, type Validation,
} from '../lib/api'
import { generateBrandedQrSvg } from '../lib/qrBrand'
import { labelStyle, purpleBtn, ghostBtn, screenTitle } from '../ui'

const overlay: CSSProperties = { position: 'fixed', inset: 0, background: 'rgba(35,31,32,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 16 }
const modal: CSSProperties = { width: 'min(560px, 94vw)', maxHeight: '92vh', overflowY: 'auto', background: '#fff', borderRadius: 16, padding: 24, boxShadow: '0 20px 60px rgba(0,0,0,0.35)' }
const DEVICES_GRID_COLUMNS = 'minmax(160px, 1fr) 170px 130px 120px 90px'

export function Stations({ session }: { session: TenantSession }) {
  const [devices, setDevices] = useState<TenantDevice[]>([])
  const [validations, setValidations] = useState<Validation[]>([])
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState('')
  // Program discovery is its own read with its own outcome. 'unknown' (not yet
  // answered) and 'error' (failed) must never be read as 'ready' with zero
  // programs: pairing is program-scoped, and only a successful empty answer is
  // the genuine "no programs" state.
  const [programsStatus, setProgramsStatus] = useState<'unknown' | 'ready' | 'error'>('unknown')
  const [programsErr, setProgramsErr] = useState('')
  const [creating, setCreating] = useState(false)
  const [newName, setNewName] = useState('')
  const [newCode, setNewCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [revealed, setRevealed] = useState<{ device: TenantDevice; token: string } | null>(null)
  const activeValidations = validations.filter(v => {
    if (!v.active) return false
    if (!v.expiresAt) return true
    const expiry = Date.parse(v.expiresAt)
    return Number.isFinite(expiry) && expiry > Date.now()
  })

  const load = async () => {
    setLoading(true); setErr('')
    try { setDevices((await listDevices(session.token)).devices) }
    catch (e) { setErr(e instanceof Error ? e.message : 'load failed'); console.error('[tenant] devices', e) }
    finally { setLoading(false) }
  }

  const loadPrograms = async () => {
    setProgramsErr('')
    try {
      setValidations(await listValidations(session.token))
      setProgramsStatus('ready')
    } catch (e) {
      // Keep any programs from an earlier success for display names, but the
      // status leaves 'ready' so pairing stays off until a read succeeds again.
      setProgramsStatus('error')
      setProgramsErr(e instanceof Error ? e.message : 'load failed'); console.error('[tenant] programs', e)
    }
  }
  const pairingBlocked = loading || programsStatus !== 'ready' || activeValidations.length === 0

  useEffect(() => {
    // A device credential 403s on both of these. It never gets here (App.tsx
    // doesn't route it, and the guard below returns null), but the effect runs
    // before that guard, so skip the calls rather than log two dead 403s.
    if (session.kind === 'device') return
    void load()
    // Pairing is program-scoped. A failed offer load must not mint an
    // unbound station, so the create action remains disabled until it succeeds.
    void loadPrograms()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Defence in depth: a device credential cannot reach these routes anyway
  // (403 server-side), and App.tsx never renders this tab for one.
  if (session.kind === 'device') return null

  const handleCreate = async () => {
    if (busy || programsStatus !== 'ready') return
    setBusy(true)
    try {
      const selected = activeValidations.find(v => v.code === newCode)
      const res = await createDevice(session.token, {
        name: newName.trim() || 'Front desk tablet',
        defaultCode: newCode || undefined,
        programId: (selected?.validationId ?? selected?.id ?? newCode) || undefined,
      })
      setRevealed(res)        // the plaintext credential, shown ONCE
      setCreating(false); setNewName(''); setNewCode('')
      await load()
    } catch (e) {
      alert(`Couldn't create the tablet link: ${e instanceof Error ? e.message : 'error'}`)
    } finally { setBusy(false) }
  }

  const handleRevoke = async (d: TenantDevice) => {
    if (!confirm(`Revoke "${d.name}"? That tablet stops validating immediately and will need a new link. This can't be undone.`)) return
    try { await revokeDevice(session.token, d.id); await load() }
    catch (e) { alert(`Revoke failed: ${e instanceof Error ? e.message : 'error'}`) }
  }

  const fmt = (iso?: string) => {
    if (!iso) return 'never'
    try { return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) }
    catch { return iso }
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
        <h2 style={screenTitle}>Tablets</h2>
        <span style={{ fontSize: 12, color: 'var(--color-gray-600)' }}>
          {loading ? 'loading…' : err ? '' : `${devices.length} tablet${devices.length === 1 ? '' : 's'}`}
        </span>
        <div style={{ flex: 1 }} />
        <button
          onClick={() => {
            setCreating(true); setNewName('')
            setNewCode(activeValidations.length === 1 ? activeValidations[0]!.code : '')
          }}
          disabled={pairingBlocked}
          style={{ ...purpleBtn, opacity: pairingBlocked ? 0.55 : 1 }}
        >+ New tablet link</button>
      </div>

      {programsStatus === 'error' && (
        <div role="alert" style={{ marginBottom: 14 }}>
          <LoadError
            title="Couldn't load validation programs"
            message={`${programsErr}. New tablet links are off until programs load, because every tablet is tied to one.`}
            onRetry={() => void loadPrograms()}
          />
        </div>
      )}
      {err && devices.length > 0 && (
        <div role="alert" style={{ marginBottom: 14 }}>
          <LoadError
            title="Couldn't refresh tablets"
            message={`${err}. The list below may be out of date.`}
            onRetry={() => void load()}
          />
        </div>
      )}
      {programsStatus === 'ready' && activeValidations.length === 0 && (
        <div style={{ marginBottom: 14, fontSize: 13, fontWeight: 600, color: 'var(--color-gray-700)' }}>
          You have no active validation programs, so there is nothing to tie a tablet to yet.
        </div>
      )}

      <div style={{ background: 'rgba(127,63,152,0.06)', border: '1px solid rgba(127,63,152,0.18)', borderRadius: 12, padding: '16px 18px' }}>
        <div style={{ fontSize: 14.5, fontWeight: 800, letterSpacing: '-0.2px', marginBottom: 4 }}>Keep a front desk tablet signed in</div>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-gray-700)', lineHeight: 1.55 }}>
          A tablet link opens the "Validate a car" screen and <b>stays signed in</b>, so nobody has to check
          email at the start of a shift. It can only look up and validate cars — it can see
          its paired validation program, because it needs that program to apply the right one, but not your redemption history
          and not this page. Treat it like a key: anyone with the link can validate parking here until you
          revoke it, and revoking takes effect on that tablet's very next tap.
        </div>
      </div>

      <div style={{ background: '#fff', borderRadius: 12, overflowX: 'auto', border: '1px solid var(--color-gray-200)', marginTop: 16 }}>
        <div style={{ display: 'grid', width: '100%', gridTemplateColumns: DEVICES_GRID_COLUMNS, padding: '10px 16px', borderBottom: '1px solid var(--color-gray-200)', fontSize: 11, fontWeight: 800, letterSpacing: '0.5px', textTransform: 'uppercase', color: 'var(--color-gray-600)' }}>
          <div>Name</div><div>Credential</div><div>Program</div><div>Last used</div><div></div>
        </div>
        {devices.length === 0 ? (
          err ? (
            <div role="alert" style={{ padding: 16 }}><LoadError message={err} onRetry={() => void load()} /></div>
          ) : (
            <div style={{ padding: 32, textAlign: 'center', fontSize: 13, color: 'var(--color-gray-600)' }}>
              {loading ? '' : 'No tablets yet. Create a link to set one up at your front desk.'}
            </div>
          )
        ) : devices.map(d => (
          <div key={d.id} style={{ display: 'grid', width: '100%', gridTemplateColumns: DEVICES_GRID_COLUMNS, alignItems: 'center', padding: '12px 16px', borderBottom: '1px solid var(--color-gray-100)', fontSize: 13 }}>
            <div style={{ fontWeight: 600, paddingRight: 8 }}>{d.name}</div>
            <code style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--color-gray-600)' }}>{d.prefix}…{d.last4}</code>
            <div style={{ fontSize: 12, color: 'var(--color-gray-600)' }}>
              {validations.find(v => (v.validationId ?? v.id ?? v.code) === (d.programId ?? d.defaultCode))?.name
                ?? d.programId
                ?? d.defaultCode
                ?? 'legacy station'}
            </div>
            <div style={{ fontSize: 12, color: 'var(--color-gray-600)' }}>{fmt(d.lastUsedAt)}</div>
            <div style={{ textAlign: 'right' }}>
              <button onClick={() => handleRevoke(d)} style={{ ...ghostBtn, fontSize: 11, color: '#b91c1c', borderColor: 'rgba(185,28,28,0.4)', padding: '5px 10px' }}>Revoke</button>
            </div>
          </div>
        ))}
      </div>

      {creating && (
        <div onClick={() => setCreating(false)} style={overlay}>
          <div onClick={e => e.stopPropagation()} style={modal}>
            <h3 style={{ fontSize: 18, fontWeight: 800, margin: '0 0 6px' }}>New tablet link</h3>
            <p style={{ fontSize: 13, color: 'var(--color-gray-600)', margin: '0 0 16px' }}>
              Name it for where the tablet lives — e.g. "Front desk — Diner on Main".
            </p>
            <div style={labelStyle}>Tablet name</div>
            <input
              value={newName} onChange={e => setNewName(e.target.value)} autoFocus
              onKeyDown={e => { if (e.key === 'Enter') void handleCreate() }}
              placeholder="Front desk tablet"
              style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1.5px solid var(--color-gray-300)', fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box' }}
            />
            <div style={{ ...labelStyle, marginTop: 16 }}>Validation program</div>
            {activeValidations.length === 1 ? (
              <div style={{ padding: '10px 12px', borderRadius: 8, border: '1.5px solid var(--color-gray-300)', fontSize: 14, background: 'var(--color-gray-100)' }}>
                {activeValidations[0]!.name || activeValidations[0]!.code}
              </div>
            ) : (
              <select
                value={newCode} onChange={e => setNewCode(e.target.value)}
                style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1.5px solid var(--color-gray-300)', fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box', background: '#fff' }}
              >
                <option value="">Choose a validation program</option>
                {activeValidations.map(v => (
                  <option key={v.code} value={v.code}>{v.name ? `${v.name} (${v.code})` : v.code}</option>
                ))}
              </select>
            )}
            <p style={{ fontSize: 12, color: 'var(--color-gray-600)', margin: '6px 0 0', lineHeight: 1.5 }}>
              This station will issue only this program. The binding stays in force if the program's code is rotated.
            </p>
            <div style={{ display: 'flex', gap: 8, marginTop: 18, justifyContent: 'flex-end' }}>
              <button onClick={() => setCreating(false)} style={ghostBtn}>Cancel</button>
              <button
                onClick={() => void handleCreate()}
                disabled={busy || programsStatus !== 'ready' || activeValidations.length === 0 || (activeValidations.length > 1 && !newCode)}
                style={{ ...purpleBtn, opacity: busy || activeValidations.length === 0 || (activeValidations.length > 1 && !newCode) ? 0.6 : 1 }}
              >{busy ? 'Creating…' : 'Create link'}</button>
            </div>
          </div>
        </div>
      )}

      {revealed && <RevealModal data={revealed} onClose={() => setRevealed(null)} />}
    </div>
  )
}

/**
 * The one-time reveal. The credential is in the URL itself, so the URL is the
 * secret — hence the same "copy it now" treatment the API-key modal gives its
 * token, plus a QR because the destination for this one is a tablet you're
 * holding rather than a config file you're pasting into.
 */
function RevealModal({ data, onClose }: { data: { device: TenantDevice; token: string }; onClose: () => void }) {
  const [copied, setCopied] = useState(false)
  const [qrSvg, setQrSvg] = useState('')
  const url = tabletLinkUrl(data.token, data.device.defaultCode)

  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const svg = await generateBrandedQrSvg(url)
        if (!cancelled) setQrSvg(svg)
      } catch { /* the QR is a convenience — the copyable URL is the real artifact */ }
    })()
    return () => { cancelled = true }
  }, [url])

  const copy = async () => {
    try { await navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 2000) }
    catch { /* clipboard blocked — the field is selectable as a fallback */ }
  }

  return (
    <div style={overlay}>
      <div onClick={e => e.stopPropagation()} style={modal}>
        <h3 style={{ fontSize: 18, fontWeight: 800, margin: '0 0 6px' }}>Open this on the tablet now</h3>
        <div style={{ background: 'rgba(241,101,33,0.10)', border: '1px solid rgba(241,101,33,0.4)', borderRadius: 10, padding: '10px 12px', fontSize: 12.5, color: '#b45309', lineHeight: 1.5, marginBottom: 14 }}>
          This is the only time we'll show this link. Open it on the tablet (or scan the code) and leave that
          browser signed in. If it's lost, revoke it and create a new one.
        </div>
        <div style={labelStyle}>{data.device.name}</div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 260px', minWidth: 0 }}>
            <input
              readOnly value={url} onFocus={e => e.currentTarget.select()}
              style={{ width: '100%', padding: '10px 12px', borderRadius: 8, fontSize: 12, fontFamily: 'monospace', border: '1.5px solid var(--color-gray-300)', background: 'var(--color-gray-100)', boxSizing: 'border-box' }}
            />
            <button onClick={copy} style={{ ...purpleBtn, marginTop: 8 }}>{copied ? 'Copied ✓' : 'Copy link'}</button>
          </div>
          {qrSvg && <div style={{ width: 140, height: 140, flexShrink: 0 }} dangerouslySetInnerHTML={{ __html: qrSvg }} />}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 20 }}>
          <button onClick={onClose} style={purpleBtn}>Done</button>
        </div>
      </div>
    </div>
  )
}

// Inline load-failure block — shown in place of the empty state so a manager
// never reads "couldn't load" as "no tablets" and mints a duplicate for a
// tablet that is still live out there. Mirrors Integrations.tsx.
function LoadError({ message, onRetry, title = "Couldn't load" }: { message: string; onRetry: () => void; title?: string }) {
  return (
    <div style={{ background: 'rgba(237,28,36,0.10)', border: '1.5px solid rgba(237,28,36,0.4)', borderRadius: 12, padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
      <div style={{ flex: 1, minWidth: 220 }}>
        <div style={{ fontSize: 14, fontWeight: 800, color: '#b91c1c', marginBottom: 2 }}>{title}</div>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-gray-700)', lineHeight: 1.5 }}>{message}</div>
      </div>
      <button onClick={onRetry} style={purpleBtn}>Retry</button>
    </div>
  )
}
